import {
    TemplateElement,
    html,
    defineElement,
    when,
    unsafeHTML,
    optionalAttribute,
    classMap,
} from '@webtides/element-js';
import style from './image-text-teaser.css';
import '../../../chip/chip.js';
import '../rive-animation/rive-animation.js';

/**
 * @property {'portrait' | 'landscape' | 'promo'} variant - Layout variant
 * @property {ImageProperties} image - Image object with src, srcset, alt
 * @property {KickerProperties} label - Label
 * @property {string} text - Rich text content (HTML)
 * @property {LinkProperties} link - Link object with url, text, type
 * @property {Object} riveAnimation - Optional Rive animation overlay config
 */
export default class ImageTextTeaser extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            variant: 'portrait',
            image: null,
            riveAnimation: null,
            text: '',
            link: null,
        };
    }

    template() {
        const isPortrait = this.variant === 'portrait';
        const isLandscape = this.variant === 'landscape';
        const isPromo = this.variant === 'promo';

        // TODO: find a more generic way to toggle card/teaser background color based on section background (group-[[bg-color="green-light25"]]/section)
        return html`
            <div
                class="flex flex-col items-start gap-fix-m mx-auto ${classMap({
                    '@md:w-6/12 @lg:w-6/12': isPortrait,
                    '@md:flex-row @md:gap-fix-xl @lg:w-10/12': isLandscape || isPromo,
                    'bg-green-light25 group-[[bg-color="green-light25"]]/section:bg-[white] p-fix-s md:p-fix-l rounded-md':
                        isPromo,
                })}"
            >
                ${when(
                    this.image?.srcset,
                    html`
                        <div
                            class="relative w-full ${classMap({
                                '@md:w-1/2 @md:shrink-0': isLandscape || isPromo,
                            })}"
                        >
                            <img
                                loading="lazy"
                                src="${this.image?.src}"
                                srcset="${this.image?.srcset}"
                                sizes="(min-width: 1440px) 560px, (min-width: 1024px) 38vw, (min-width: 768px) 45vw, 100vw"
                                alt="${this.image?.alt || ''}"
                                class="w-full object-cover rounded-md aspect-[4/3] @md:aspect-[16/10]"
                            />
                            ${when(
                                this.label && this.label.text,
                                html`
                                    <mb-chip
                                        variant="${this.label.variant || 'gradient'}"
                                        text="${this.label.text}"
                                        class="absolute left-fix-xs top-fix-xs mb-fix-2xs md:mb-fix-s mb-chip-size-sm lg:mb-chip-size-md"
                                    ></mb-chip>
                                `,
                            )}
                            ${when(
                                this.riveAnimation?.src,
                                html`
                                    <mb-rive-animation
                                        class="absolute inset-0"
                                        src="${this.riveAnimation?.src}"
                                        artboard="${this.riveAnimation?.artboard || ''}"
                                        state-machine="${this.riveAnimation?.stateMachine || ''}"
                                        fit="${this.riveAnimation?.fit || 'contain'}"
                                        alignment="${this.riveAnimation?.alignment || 'center'}"
                                        trigger="${this.riveAnimation?.trigger || 'onView'}"
                                        pause-out-of-view="${this.riveAnimation?.pauseOutOfView ?? true}"
                                    ></mb-rive-animation>
                                `,
                            )}
                        </div>
                    `,
                )}
                <div class="flex flex-col gap-fix-s">
                    ${when(this.text, html`<div class="richtext">${unsafeHTML(this.text)}</div>`)}
                    ${when(
                        this.link?.url && this.link?.text,
                        html`
                            <a
                                class="arrow-link fix-body3-medium text-green-medium hover:text-green-dark"
                                href="${this.link?.url}"
                                ${optionalAttribute(this.link?.type === 'external', 'target', '_blank')}
                                element="image text teaser"
                                ${optionalAttribute(
                                    this.link?.trackingLabel,
                                    'tracking-label',
                                    this.link?.trackingLabel,
                                )}
                                >${this.link?.text}</a
                            >
                        `,
                    )}
                </div>
            </div>
        `;
    }
}

defineElement('mb-image-text-teaser', ImageTextTeaser);
